import { useTranslation } from 'react-i18next';

/**
 * @param {{
 *   id: string;
 *   label: string;
 *   value: string;
 *   onChange: (v: string) => void;
 *   type?: string;
 *   disabled?: boolean;
 *   required?: boolean;
 *   error?: string;
 * }} props
 */
export function ProfileFormField({
    id,
    label,
    value,
    onChange,
    type = 'text',
    disabled,
    required = false,
    error = '',
}) {
    return (
        <div className="mb-3">
            <label htmlFor={id} className="form-label login-field-label">
                {label}
                {required ? (
                    <span className="text-danger ms-1" aria-hidden="true">
                        *
                    </span>
                ) : null}
            </label>
            <input
                id={id}
                type={type}
                className={`form-control login-field-input${error ? ' is-invalid' : ''}`}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={error ? `${id}-error` : undefined}
            />
            {error ? (
                <div
                    id={`${id}-error`}
                    className="invalid-feedback d-block"
                    role="alert"
                >
                    {error}
                </div>
            ) : null}
        </div>
    );
}

/**
 * @param {{
 *   idPrefix: string;
 *   form: Record<string, string>;
 *   setField: (key: string, value: string) => void;
 *   submitting?: boolean;
 *   fieldErrors?: Record<string, string>;
 * }} props
 */
export function MemberProfileFormFields({
    idPrefix,
    form,
    setField,
    submitting = false,
    fieldErrors = {},
}) {
    const { t } = useTranslation();

    return (
        <>
            <p className="edit-spouse-modal-section-label small text-secondary mb-2">
                {t('profilePage.editSpouseSectionPersonal')}
            </p>
            <div className="row g-3">
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-first-name`}
                        label={t('profilePage.addChildFirstName')}
                        value={form.firstName}
                        onChange={(v) => setField('firstName', v)}
                        disabled={submitting}
                        required
                        error={fieldErrors.firstName}
                    />
                </div>
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-last-name`}
                        label={t('profilePage.addChildLastName')}
                        value={form.lastName}
                        onChange={(v) => setField('lastName', v)}
                        disabled={submitting}
                        required
                        error={fieldErrors.lastName}
                    />
                </div>
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-birth-date`}
                        label={t('profilePage.addChildBirthDate')}
                        type="date"
                        value={form.birthDate}
                        onChange={(v) => setField('birthDate', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-tc`}
                        label={t('profilePage.editSpouseTcKimlikNo')}
                        value={form.tcKimlikNo}
                        onChange={(v) => setField('tcKimlikNo', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-phone`}
                        label={t('profilePage.editSpousePhone')}
                        value={form.phoneNumber}
                        onChange={(v) => setField('phoneNumber', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-email`}
                        label={t('profilePage.editSpouseEmail')}
                        type="email"
                        value={form.email}
                        onChange={(v) => setField('email', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-12">
                    <ProfileFormField
                        id={`${idPrefix}-address`}
                        label={t('profilePage.editSpouseAddress')}
                        value={form.address}
                        onChange={(v) => setField('address', v)}
                        disabled={submitting}
                    />
                </div>
            </div>

            <p className="edit-spouse-modal-section-label small text-secondary mb-2 mt-2">
                {t('profilePage.editSpouseSectionWork')}
            </p>
            <div className="row g-3">
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-job-title`}
                        label={t('profilePage.editSpouseJobTitle')}
                        value={form.jobTitle}
                        onChange={(v) => setField('jobTitle', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-company`}
                        label={t('profilePage.editSpouseCompany')}
                        value={form.company}
                        onChange={(v) => setField('company', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-sector`}
                        label={t('profilePage.editSpouseSector')}
                        value={form.sector}
                        onChange={(v) => setField('sector', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-club-join`}
                        label={t('profilePage.editSpouseClubJoinDate')}
                        type="date"
                        value={form.clubJoinDate}
                        onChange={(v) => setField('clubJoinDate', v)}
                        disabled={submitting}
                        required
                        error={fieldErrors.clubJoinDate}
                    />
                </div>
                <div className="col-12">
                    <ProfileFormField
                        id={`${idPrefix}-work-address`}
                        label={t('profilePage.editSpouseWorkAddress')}
                        value={form.workAddress}
                        onChange={(v) => setField('workAddress', v)}
                        disabled={submitting}
                    />
                </div>
            </div>

            <p className="edit-spouse-modal-section-label small text-secondary mb-2 mt-2">
                {t('profilePage.editSpouseSectionEducation')}
            </p>
            <div className="row g-3">
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-university`}
                        label={t('profilePage.editSpouseUniversity')}
                        value={form.university}
                        onChange={(v) => setField('university', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-high-school`}
                        label={t('profilePage.editSpouseHighSchool')}
                        value={form.highSchool}
                        onChange={(v) => setField('highSchool', v)}
                        disabled={submitting}
                    />
                </div>
            </div>

            <p className="edit-spouse-modal-section-label small text-secondary mb-2 mt-2">
                {t('profilePage.editSpouseSectionOther')}
            </p>
            <div className="row g-3">
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-website`}
                        label={t('profilePage.editSpouseWebsite')}
                        value={form.website}
                        onChange={(v) => setField('website', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-6">
                    <ProfileFormField
                        id={`${idPrefix}-hobbies`}
                        label={t('profilePage.editSpouseHobbies')}
                        value={form.hobbies}
                        onChange={(v) => setField('hobbies', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-4">
                    <ProfileFormField
                        id={`${idPrefix}-facebook`}
                        label="Facebook"
                        value={form.facebook}
                        onChange={(v) => setField('facebook', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-4">
                    <ProfileFormField
                        id={`${idPrefix}-twitter`}
                        label="X (Twitter)"
                        value={form.twitter}
                        onChange={(v) => setField('twitter', v)}
                        disabled={submitting}
                    />
                </div>
                <div className="col-sm-4">
                    <ProfileFormField
                        id={`${idPrefix}-linkedin`}
                        label="LinkedIn"
                        value={form.linkedIn}
                        onChange={(v) => setField('linkedIn', v)}
                        disabled={submitting}
                    />
                </div>
            </div>
        </>
    );
}
